import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@appcaire/ui";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import { brandPreview } from "./lib/brandPreview";
import { watchPublishedBrand } from "@appcaire/brand/runtime";
import "./i18n";
import { setupI18nMiddleware } from "./middleware/i18nMiddleware";
import { I18nProvider } from "./components/I18nProvider";
import { shouldHydrate } from "./lib/ssrHydration";

// Set up middleware immediately
setupI18nMiddleware();
const stopBrandWatch = watchPublishedBrand(brandPreview, "/api/public-brand");
import.meta.hot?.dispose(stopBrandWatch);

/**
 * Client-side entry point for hydration
 * This is used in SSR mode to hydrate the server-rendered HTML
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
});

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

const tree = (
  <HelmetProvider>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </I18nProvider>
  </HelmetProvider>
);

// Hydrate the SSR markup in production; client-render under `vite dev`, which
// serves index.html with its `<!--app-html-->` marker un-replaced. See
// shouldHydrate for why this is not `hasChildNodes()`.
if (shouldHydrate(root)) {
  hydrateRoot(root, tree);
} else {
  createRoot(root).render(tree);
}
